import Phaser from 'phaser';
import {
  HUD_REGISTRY_KEY,
  formatScore,
  starRow,
  livesRow,
  hpBarWidths,
  eliteBarState,
  type HudPayload,
  type PlayerHudData,
} from '@/systems/HudData';
import { STAR_CAP } from '@/systems/StarMeter';

const FONT = '"Courier New", monospace';
const HP_W = 180;
const HP_H = 13;
const PORT_W = 44;
const PORT_H = 66;
const HUD_DEPTH = 600;

const BACKUP_BAR_H = 5;
const BACKUP_COLOR = 0x00ffcc;

interface PlayerPanel {
  portrait: Phaser.GameObjects.Rectangle;
  hpBg: Phaser.GameObjects.Rectangle;
  hpRed: Phaser.GameObjects.Rectangle;
  hpGreen: Phaser.GameObjects.Rectangle;
  starsText: Phaser.GameObjects.Text;
  livesText: Phaser.GameObjects.Text;
  backupBg: Phaser.GameObjects.Rectangle;
  backupFg: Phaser.GameObjects.Rectangle;
  scoreText: Phaser.GameObjects.Text;
  comboText: Phaser.GameObjects.Text;
}

interface BossBar {
  bg: Phaser.GameObjects.Rectangle;
  fg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
}

/**
 * HudScene — screen-pinned HUD rendered in parallel with StageScene.
 *
 * Reads HudPayload from the shared Phaser registry each frame.
 * Layout: P1 panel top-left, P2 panel top-right, stage/wave centre top,
 * boss bar bottom (hidden until a boss is present).
 *
 * TODO(MY-1454): swap FONT for a loaded pixel bitmap font when art pipeline lands.
 * TODO(MY-1454): P2 HP bar fill direction should mirror right-to-left (SoR authentic).
 */
/** How many frames the lives text flashes gold after a 1-up. */
const EXTRA_LIFE_FLASH_FRAMES = 60;
const LIVES_COLOR_NORMAL = '#ff6b6b';
const LIVES_COLOR_FLASH = '#ffd700';

export class HudScene extends Phaser.Scene {
  private p1!: PlayerPanel;

  private p2!: PlayerPanel;

  private stageNameText!: Phaser.GameObjects.Text;

  private waveText!: Phaser.GameObjects.Text;

  private multiplierText!: Phaser.GameObjects.Text;

  private boss!: BossBar;

  private elite!: BossBar;

  private extraLifeFlashFrames = 0;

  constructor() {
    super({ key: 'HudScene' });
  }

  create(): void {
    const { width, height } = this.scale;
    this.p1 = this.buildLeftPanel(4);
    this.p2 = this.buildRightPanel(width - 4);
    this.setPanelVisible(this.p2, false);

    // Centre top: stage name + wave counter.
    this.stageNameText = this.add
      .text(width / 2, 6, '', {
        fontFamily: FONT,
        fontSize: '18px',
        color: '#ef233c',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setOrigin(0.5, 0)
      .setDepth(HUD_DEPTH);

    this.waveText = this.add
      .text(width / 2, 28, '', {
        fontFamily: FONT,
        fontSize: '12px',
        color: '#aaaaaa',
      })
      .setOrigin(0.5, 0)
      .setDepth(HUD_DEPTH);

    // Score Attack stage multiplier banner (hidden unless multiplier > 1).
    this.multiplierText = this.add
      .text(width / 2, 44, '', {
        fontFamily: FONT,
        fontSize: '14px',
        color: '#ffd700',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setOrigin(0.5, 0)
      .setDepth(HUD_DEPTH);

    // Boss bar — bottom centre, hidden by default.
    this.boss = this.buildBossBar(width, height);
    this.setBossVisible(false);

    // Elite bar — just above the boss bar, hidden by default.
    this.elite = this.buildEliteBar(width, height);
    this.setEliteVisible(false);
  }

  update(): void {
    const payload = this.registry.get(HUD_REGISTRY_KEY) as HudPayload | undefined;
    if (!payload) return;

    if (payload.extraLifeFlash) {
      this.extraLifeFlashFrames = EXTRA_LIFE_FLASH_FRAMES;
    }
    const flashing = this.extraLifeFlashFrames > 0;
    if (flashing) this.extraLifeFlashFrames -= 1;

    this.refreshPanel(this.p1, payload.p1);

    if (payload.p2) {
      this.setPanelVisible(this.p2, true);
      this.refreshPanel(this.p2, payload.p2);
    } else {
      this.setPanelVisible(this.p2, false);
    }

    // Apply 1-up flash colour over the lives text written by refreshPanel.
    const livesColor = flashing ? LIVES_COLOR_FLASH : LIVES_COLOR_NORMAL;
    this.p1.livesText.setColor(livesColor);
    if (payload.p2) this.p2.livesText.setColor(livesColor);

    this.stageNameText.setText(payload.stageName);
    this.waveText.setText(payload.wave > 0 ? `WAVE ${payload.wave}` : '');
    this.multiplierText.setText(
      payload.scoreMultiplier > 1 ? `SCORE ×${payload.scoreMultiplier.toFixed(1)}` : '',
    );

    const hasBoss = payload.bossHpFrac > 0;
    this.setBossVisible(hasBoss);
    if (hasBoss) {
      const bossW = Math.round(Math.max(0, Math.min(1, payload.bossHpFrac)) * this.boss.bg.width);
      this.boss.fg.setSize(bossW, HP_H);
    }

    const elite = eliteBarState(payload.eliteHpFrac, payload.eliteName);
    this.setEliteVisible(elite.visible);
    if (elite.visible) {
      const eliteW = Math.round(elite.hpFrac * this.elite.bg.width);
      this.elite.fg.setSize(eliteW, HP_H);
      this.elite.label.setText(elite.name);
    }
  }

  // ── panel builders ────────────────────────────────────────────────────────

  private buildLeftPanel(leftEdge: number): PlayerPanel {
    const x = leftEdge;
    const y = 4;
    const hpX = x + PORT_W + 4;

    const portrait = this.add
      .rectangle(x, y, PORT_W, PORT_H, 0x444444)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH);
    const hpBg = this.add
      .rectangle(hpX, y, HP_W, HP_H, 0x222222)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH);
    const hpRed = this.add
      .rectangle(hpX, y, HP_W, HP_H, 0xef233c)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH + 1);
    const hpGreen = this.add
      .rectangle(hpX, y, 0, HP_H, 0x00ff66)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH + 1);
    const starsText = this.add
      .text(hpX, y + HP_H + 3, '', { fontFamily: FONT, fontSize: '12px', color: '#ffd700' })
      .setDepth(HUD_DEPTH);
    const livesText = this.add
      .text(hpX, y + HP_H + 19, '', { fontFamily: FONT, fontSize: '11px', color: '#ff6b6b' })
      .setDepth(HUD_DEPTH);
    const backupY = y + HP_H + 33;
    const backupBg = this.add
      .rectangle(hpX, backupY, HP_W, BACKUP_BAR_H, 0x222222)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH);
    const backupFg = this.add
      .rectangle(hpX, backupY, 0, BACKUP_BAR_H, BACKUP_COLOR)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH + 1);
    const scoreText = this.add
      .text(x, y + PORT_H + 4, '', { fontFamily: FONT, fontSize: '14px', color: '#ffffff' })
      .setDepth(HUD_DEPTH);
    const comboText = this.add
      .text(x, y + PORT_H + 20, '', { fontFamily: FONT, fontSize: '12px', color: '#ffdd00' })
      .setDepth(HUD_DEPTH);

    return {
      portrait,
      hpBg,
      hpRed,
      hpGreen,
      starsText,
      livesText,
      backupBg,
      backupFg,
      scoreText,
      comboText,
    };
  }

  private buildRightPanel(rightEdge: number): PlayerPanel {
    const y = 4;
    const portraitX = rightEdge - PORT_W;
    const hpX = portraitX - HP_W - 4;

    const portrait = this.add
      .rectangle(portraitX, y, PORT_W, PORT_H, 0x444444)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH);
    const hpBg = this.add
      .rectangle(hpX, y, HP_W, HP_H, 0x222222)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH);
    const hpRed = this.add
      .rectangle(hpX, y, HP_W, HP_H, 0xef233c)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH + 1);
    const hpGreen = this.add
      .rectangle(hpX, y, 0, HP_H, 0x00ff66)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH + 1);
    const starsText = this.add
      .text(portraitX + PORT_W, y + HP_H + 3, '', {
        fontFamily: FONT,
        fontSize: '12px',
        color: '#ffd700',
      })
      .setOrigin(1, 0)
      .setDepth(HUD_DEPTH);
    const livesText = this.add
      .text(portraitX + PORT_W, y + HP_H + 19, '', {
        fontFamily: FONT,
        fontSize: '11px',
        color: '#ff6b6b',
      })
      .setOrigin(1, 0)
      .setDepth(HUD_DEPTH);
    const backupY = y + HP_H + 33;
    const backupBg = this.add
      .rectangle(hpX, backupY, HP_W, BACKUP_BAR_H, 0x222222)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH);
    const backupFg = this.add
      .rectangle(hpX, backupY, 0, BACKUP_BAR_H, BACKUP_COLOR)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH + 1);
    const scoreText = this.add
      .text(rightEdge, y + PORT_H + 4, '', {
        fontFamily: FONT,
        fontSize: '14px',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setDepth(HUD_DEPTH);
    const comboText = this.add
      .text(rightEdge, y + PORT_H + 20, '', {
        fontFamily: FONT,
        fontSize: '12px',
        color: '#ffdd00',
      })
      .setOrigin(1, 0)
      .setDepth(HUD_DEPTH);

    return {
      portrait,
      hpBg,
      hpRed,
      hpGreen,
      starsText,
      livesText,
      backupBg,
      backupFg,
      scoreText,
      comboText,
    };
  }

  private buildBossBar(width: number, height: number): BossBar {
    const barW = 960;
    const barX = (width - barW) / 2;
    const barY = height - 26;

    const label = this.add
      .text(barX, barY - 14, 'BOSS', { fontFamily: FONT, fontSize: '11px', color: '#ef233c' })
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH);
    const bg = this.add
      .rectangle(barX, barY, barW, HP_H, 0x222222)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH);
    const fg = this.add
      .rectangle(barX, barY, barW, HP_H, 0xff4444)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH + 1);

    return { bg, fg, label };
  }

  private buildEliteBar(width: number, height: number): BossBar {
    const barW = 960;
    const barX = (width - barW) / 2;
    // Sit 26px above the boss bar (boss bar is at height - 26; this is at height - 52).
    const barY = height - 52;

    const label = this.add
      .text(barX, barY - 14, '', { fontFamily: FONT, fontSize: '11px', color: '#ff8c00' })
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH);
    const bg = this.add
      .rectangle(barX, barY, barW, HP_H, 0x222222)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH);
    const fg = this.add
      .rectangle(barX, barY, barW, HP_H, 0xff8c00)
      .setOrigin(0, 0)
      .setDepth(HUD_DEPTH + 1);

    return { bg, fg, label };
  }

  // ── refresh helpers ───────────────────────────────────────────────────────

  private refreshPanel(panel: PlayerPanel, data: PlayerHudData): void {
    panel.portrait.setFillStyle(data.heroColor);

    const { redW, greenW } = hpBarWidths(data.hpFrac, data.greenHpFrac, HP_W);
    panel.hpRed.setSize(redW, HP_H);
    panel.hpGreen.x = panel.hpBg.x + redW;
    panel.hpGreen.setSize(greenW, HP_H);

    panel.starsText.setText(starRow(data.stars, STAR_CAP));
    panel.livesText.setText(livesRow(data.lives));

    const backupW = Math.round((data.backupFrac ?? 0) * HP_W);
    panel.backupFg.setSize(backupW, BACKUP_BAR_H);

    panel.scoreText.setText(formatScore(data.score));

    const combo = data.styleCombo ?? 0;
    const grade = data.styleGrade ?? 'D';
    panel.comboText.setText(combo > 1 ? `${combo} HIT  ${grade}` : grade);
  }

  private setPanelVisible(panel: PlayerPanel, visible: boolean): void {
    panel.portrait.setVisible(visible);
    panel.hpBg.setVisible(visible);
    panel.hpRed.setVisible(visible);
    panel.hpGreen.setVisible(visible);
    panel.starsText.setVisible(visible);
    panel.livesText.setVisible(visible);
    panel.backupBg.setVisible(visible);
    panel.backupFg.setVisible(visible);
    panel.scoreText.setVisible(visible);
    panel.comboText.setVisible(visible);
  }

  private setBossVisible(visible: boolean): void {
    this.boss.bg.setVisible(visible);
    this.boss.fg.setVisible(visible);
    this.boss.label.setVisible(visible);
  }

  private setEliteVisible(visible: boolean): void {
    this.elite.bg.setVisible(visible);
    this.elite.fg.setVisible(visible);
    this.elite.label.setVisible(visible);
  }
}
